/** Client wrappers for the LMN-flow endpoints:
 *
 *  - POST /greenfield/lmn/draft                 (create an editable letter)
 *  - POST /greenfield/lmn/check-for-attachment  (would this PA accept a letter?)
 *  - POST /greenfield/lmn/sign-and-attach       (sign + upload to the PA case)
 *  - POST /greenfield/markdown/docx             (render a .docx for download)
 *
 *  The JSON calls go through the generated SDK so request/response shapes
 *  stay pinned to the Pydantic models. The docx call uses axios directly
 *  because it returns binary.
 */

import axios, { isAxiosError } from 'axios';
import { z } from 'zod';

import {
  checkWouldPaAcceptAttachmentLmn as sdkCheckAttachAvailability,
  draftLmn as sdkDraftLmn,
  signAndAttachLmn as sdkSignAndAttachLmn,
} from '@lotushealth/lotushealth_js_sdk/endpoints';
import type {
  AttachLmnInputT,
  CheckWouldPAAcceptAttachmentInputT,
  CheckWouldPAAcceptAttachmentResponseT,
  DraftLmnInputT,
  LmnCaseDraftT,
  PriorAuthCommandResponseT,
  RenderDocxInputT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { createLotusRequestConfig } from '@/shared/api/lotus/client';
import { streamJsonlEvents } from '@/shared/lib/streaming';

export type {
  CheckWouldPAAcceptAttachmentResponseT,
  LmnCaseDraftT,
  PriorAuthCommandResponseT,
  RenderDocxInputT,
};

type PatientScoped<T> = T & { patient_user_id: string };
export type AttachLmnInput = PatientScoped<AttachLmnInputT>;
export type CheckWouldPAAcceptAttachmentInput = PatientScoped<CheckWouldPAAcceptAttachmentInputT>;
export type DraftLmnInput = PatientScoped<DraftLmnInputT>;

const lmnDraftStreamEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('progress'), message: z.string() }),
  z.object({
    type: z.literal('complete'),
    draft: z.object({
      prior_auth_record_id: z.string(),
      case_number: z.string(),
      markdown: z.string(),
      summary: z.string(),
      flagged_gaps: z.array(z.string()),
    }),
  }),
]);

export type LmnDraftStreamEvent = z.infer<typeof lmnDraftStreamEventSchema>;

export async function draftLmn(body: DraftLmnInput, signal?: AbortSignal): Promise<LmnCaseDraftT> {
  const config = await createLotusRequestConfig({ signal });
  const response = await sdkDraftLmn(body, config);
  return response.data;
}

export async function* streamLmnDraft(
  body: DraftLmnInput,
  signal?: AbortSignal
): AsyncGenerator<LmnDraftStreamEvent> {
  yield* streamJsonlEvents('/greenfield/lmn/draft/stream', {}, lmnDraftStreamEventSchema, {
    method: 'POST',
    body,
    signal,
  });
}

/** Whether the PA case will accept a letter right now, with a
 *  clinician-facing reason when it won't. The backend owns the rules;
 *  the card just renders the message. */
export async function checkLmnAttachAvailability(
  body: CheckWouldPAAcceptAttachmentInput,
  signal?: AbortSignal
): Promise<CheckWouldPAAcceptAttachmentResponseT> {
  const config = await createLotusRequestConfig({ signal });
  const response = await sdkCheckAttachAvailability(body, config);
  return response.data;
}

export async function signAndAttachLmn(
  body: AttachLmnInput,
  signal?: AbortSignal
): Promise<PriorAuthCommandResponseT> {
  const config = await createLotusRequestConfig({ signal });
  const response = await sdkSignAndAttachLmn(body, config);
  return response.data;
}

/** Render the markdown to a .docx and trigger the browser's Save
 *  dialog. The filename rides in the body so the server can echo it in
 *  Content-Disposition. */
export async function downloadLmnDocx(body: RenderDocxInputT, signal?: AbortSignal): Promise<void> {
  const config = await createLotusRequestConfig({ signal });
  const response = await axios.post<Blob>(`${config.baseURL}/greenfield/markdown/docx`, body, {
    ...config,
    responseType: 'blob',
  });
  triggerBrowserDownload(response.data, body.filename);
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Safari needs the URL to outlive the click; revoke on the next tick.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
  }
}

/** Pull a clinician-facing message out of an LMN-flow error. Lotus errors
 *  carry ``{message}`` at the root; everything else falls back to the
 *  status code so the toast at least confirms the request reached the
 *  server (error bodies are unreadable when CORS strips them in dev). */
export function extractLmnErrorDetail(err: unknown): string {
  if (isAxiosError(err)) {
    const message = (err.response?.data as { message?: unknown } | undefined)?.message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
    const status = err.response?.status;
    if (typeof status === 'number') {
      return `The server rejected the request (${status}).`;
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return 'Something went wrong.';
}
